import type { MockMessageButton } from "@/dummy";
import { type DemoTranslator, rawStr } from "@/dummy/translator";

/**
 * Picks a canned AI reply for demo mode. Pattern-matches the visitor's
 * text against a few intents; falls back to a generic "let me check"
 * line. Returned reply may carry follow-up quick-reply buttons so the
 * conversation can keep going without typing.
 *
 * Reply text + button labels are localised via the demo `messenger.replies.*`
 * namespace (read off the passed `DemoTranslator`). The intent *keywords*
 * are still English-only — non-Latin / localised input falls through to
 * the (localised) fallback reply, which is acceptable for the demo.
 */
export function pickDummyReply(
  text: string,
  t: DemoTranslator,
): { text: string; buttons?: MockMessageButton[] } {
  const lower = text.toLowerCase();
  if (lower.includes("pric")) {
    return {
      text: rawStr(t, "messenger.replies.pricing.text"),
      buttons: buttons(t, [
        ["messenger.replies.pricing.planBtn", "plans"],
        ["messenger.replies.pricing.salesBtn", "sales"],
      ]),
    };
  }
  if (lower.includes("gaia") || lower.includes("ai")) {
    return {
      text: rawStr(t, "messenger.replies.gaia.text"),
      buttons: buttons(t, [["messenger.replies.gaia.handoffBtn", "handoff-flow"]]),
    };
  }
  if (lower.includes("human") || lower.includes("chat with") || lower.includes("operator")) {
    return { text: rawStr(t, "messenger.replies.human.text") };
  }
  return { text: rawStr(t, "messenger.replies.fallback.text") };
}

/** The canned reply to a demo voice note (`""` until the demo bundle has landed). */
export function pickVoiceNoteReply(t: DemoTranslator): string {
  return rawStr(t, "messenger.replies.voice.text");
}

/** Follow-up chips, minus any whose label is blank (bundle not landed): `undefined` when none. */
function buttons(t: DemoTranslator, defs: [string, string][]): MockMessageButton[] | undefined {
  const list = defs
    .map(([key, payload]) => ({ text: rawStr(t, key), payload }))
    .filter((b) => b.text !== "");
  return list.length > 0 ? list : undefined;
}

/**
 * True once the demo bundle has landed. `useDemoContent()` hands out an EMPTY translator until
 * then, and a reply picked from it is blank, so the peek replay waits for this.
 */
export function isDemoReplyReady(t: DemoTranslator): boolean {
  return rawStr(t, "messenger.replies.fallback.text") !== "";
}
